import { useEffect, useState } from "react";

const getFallbackVisibility = (element, minimumVisibleRatio, topOffset) => {
    const rect = element.getBoundingClientRect();
    const visibleWidth =
        Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
    const visibleHeight =
        Math.min(rect.bottom, window.innerHeight) -
        Math.max(rect.top, topOffset);
    const visibleArea = Math.max(0, visibleWidth) * Math.max(0, visibleHeight);
    const totalArea = rect.width * rect.height;

    if (!totalArea) return false;

    return visibleArea / totalArea >= minimumVisibleRatio;
};

const useElementInView = (targetRef, options = {}) => {
    const {
        root = null,
        rootMargin = "0px",
        threshold = 0,
        minimumVisibleRatio = threshold || 0,
        topOffset = 0,
    } = options;
    const [isInView, setIsInView] = useState(true);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const element = targetRef?.current;

        if (!element) {
            setIsInView(true);
            return undefined;
        }

        if ("IntersectionObserver" in window) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsInView(
                        Boolean(entry?.isIntersecting) &&
                            entry.intersectionRatio >= minimumVisibleRatio,
                    );
                },
                {
                    root,
                    rootMargin,
                    threshold,
                },
            );

            observer.observe(element);

            return () => {
                observer.disconnect();
            };
        }

        const handleVisibilityCheck = () => {
            setIsInView(
                getFallbackVisibility(element, minimumVisibleRatio, topOffset),
            );
        };

        handleVisibilityCheck();
        window.addEventListener("scroll", handleVisibilityCheck, {
            passive: true,
        });
        window.addEventListener("resize", handleVisibilityCheck);

        return () => {
            window.removeEventListener("scroll", handleVisibilityCheck);
            window.removeEventListener("resize", handleVisibilityCheck);
        };
    }, [minimumVisibleRatio, root, rootMargin, targetRef, threshold, topOffset]);

    return isInView;
};

export default useElementInView;
